// Story 8.10 (ADO-2019), Q3/DV-62: what the after-accept screen lists under "Not added". Only
// what the Shopper did not already see on the review. The server reports a line with no kept
// substitute with its status (Discontinued or Unavailable); when the review showed that line as
// needing attention with nothing kept (no substitute offered, or the Shopper removed it), it was
// disclosed there and is left out. A line Ready on the review that changed by the accept is
// listed, as is everything else that was going to be added and was not, with a red pill and the
// reason in words (the Figma copy for "No longer available" and "Cart limit reached").

import {
  CART_LINE_MAX_CASES,
  type HhtLineNotAdded,
  type HhtOrderReview,
} from '../../api/hht-orders.client';

/** One "Not added" card: the product, its pill and the reason. */
export interface HhtNotAddedCard {
  readonly line: HhtLineNotAdded;
  readonly pill: string;
  readonly detail: string;
}

const NO_LONGER_AVAILABLE = {
  pill: 'No longer available',
  detail:
    'Became unavailable after you reviewed this order. Search for an alternative when you next shop.',
};

const CASES = new Intl.NumberFormat('en-IE');

function cartLimit(line: HhtLineNotAdded): { pill: string; detail: string } {
  const cart = line.depotName === null ? 'Your cart' : `Your ${line.depotName} cart`;
  return {
    pill: 'Cart limit reached',
    detail: `${cart} already holds the maximum ${CASES.format(CART_LINE_MAX_CASES)} cases of this line.`,
  };
}

/** Story 8.3 (ADO-2012), D-23: card 08's pill and detail, word for word (DV-3). The number is
 *  the cart line's quantity now, not this line's. Without it (the server always sends it, so
 *  only an older or broken answer), the detail leaves out "it's now N" rather than the line. */
function changedInCart(currentQuantity: number | undefined): { pill: string; detail: string } {
  const now = currentQuantity === undefined ? '' : ` — it's now ${currentQuantity}`;
  return {
    pill: 'Changed in your cart',
    detail: `This line changed in your cart while you were reviewing${now}. Not added, so nothing was doubled. Add more if you need to.`,
  };
}

const PRICE_UNAVAILABLE = {
  pill: 'Price unavailable',
  detail: 'Failed to get a price for this line.',
};

/**
 * The "Not added" cards for an accept of `review`, given the substitutes the Shopper removed
 * there. A reason this client does not know is left out (the wire field is a string).
 */
export function hhtNotAddedCards(
  notAdded: readonly HhtLineNotAdded[],
  review: HhtOrderReview,
  removedSubstitutes: ReadonlySet<string>,
): HhtNotAddedCard[] {
  // The lines the review showed as needing attention with nothing kept for them.
  const disclosed = new Set(
    review.attention
      .filter((line) => line.substitute === null || removedSubstitutes.has(line.skuCode))
      .map((line) => line.skuCode),
  );
  return notAdded.flatMap((line): HhtNotAddedCard[] => {
    switch (line.reason) {
      case 'DISCONTINUED':
      case 'UNAVAILABLE':
        return disclosed.has(line.skuCode) ? [] : [{ line, ...NO_LONGER_AVAILABLE }];
      case 'SUBSTITUTE_NO_LONGER_AVAILABLE':
        return [{ line, ...NO_LONGER_AVAILABLE }];
      case 'QUANTITY_LIMIT':
        return [{ line, ...cartLimit(line) }];
      case 'PRICE_UNRESOLVABLE':
        return [{ line, ...PRICE_UNAVAILABLE }];
      // Story 8.3 (ADO-2012), D-23: the review did not show that the cart changed, so it is
      // always listed, with or without the quantity now: a line not added is never left out.
      case 'CHANGED_IN_CART':
        return [{ line, ...changedInCart(line.currentQuantity) }];
      default:
        return [];
    }
  });
}
